// Global
import React from 'react';

import { GeneralCardComponent } from './GeneralCard';
import { CardListing } from 'src/.generated/CardListing/GeneralCard.model';

import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useImageWrapper } from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { CardFields } from '../CardListingShared';
import { fieldTypes } from '../CardListingShared';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { useCtaFields } from 'components/helpers/Atoms/Cta/Cta.sitecore';
import { ComponentParams, GetComponentServerProps } from '@sitecore-content-sdk/nextjs';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import {
  CARD_FALLBACK_IMAGE_PATHS,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
} from 'lib/utils/components/content-list-data';

type GeneralCardStaticProps = {
  fallbackImageUrl?: string;
};

type GeneralCardParams = ComponentParams & {
  gridAspectRatio?: string;
  showLabel?: string | undefined;
};

type GeneralCardProps = CardListing.GeneralCard.GeneralCard & {
  fieldsToShow: CardFields[];
  gridAspectRatio: ImageAspectRatio;
  cardAlignment?: ContentGridCardAlignment;
  params?: GeneralCardParams;
  staticProps?: GeneralCardStaticProps | null;
};

export default function GeneralCard(props: GeneralCardProps) {
  const fallbackImageUrl = props.staticProps?.fallbackImageUrl;
  const contentImage = useImageWrapper({
    field: props.fields?.contentImage,
    fallbacks: fallbackImageUrl
      ? [
          {
            value: {
              src: fallbackImageUrl,
              alt: '',
              width: 600,
              height: 450,
            },
          },
        ]
      : undefined,
  });
  const imageLabel = usePlainTextWrapper({ field: props.fields?.imageLabel });
  const eyebrow = usePlainTextWrapper({ field: props.fields?.eyebrow });
  const contentTitle = usePlainTextWrapper({ field: props.fields?.contentTitle });
  const description = useRichTextWrapper({
    field: props.fields?.description,
    options: { maxCharacters: 260 },
  });

  const { cta1, cta2 } = useCtaFields({
    fields: props.fields,
    ctaStyleOverrides: {
      cta1: 'filled',
      cta2: 'link',
    },
    ctaVariantOverrides: {
      cta1: 'brand',
      cta2: 'brand',
    },
  });
  const isEditing = useIsEditing();

  if (!props.fields) {
    return <></>;
  }

  const fieldsToShow = props?.fieldsToShow ?? [...fieldTypes];
  const cardURL = props.fields?.cardURL?.value.href;
  const cardTarget = props.fields?.cardURL?.value.target;
  const showLabel = props.params?.showLabel === '1';
  const card = (
    <GeneralCardComponent
      gridAspectRatio={props.gridAspectRatio}
      contentImage={contentImage}
      fieldsToShow={fieldsToShow}
      imageLabel={imageLabel}
      showLabel={showLabel}
      eyebrow={eyebrow}
      title={contentTitle}
      description={description}
      cardURL={cardURL}
      cardTarget={cardTarget}
      alignment={props.cardAlignment}
      cta1={cta1}
      cta2={cta2}
    />
  );
  if (isEditing) {
    return (
      <div>
        <EditingHelpText>
          Card URL (text is ignored, only appears in editing mode):
          <LinkWrapper field={props.fields?.cardURL} ctaStyle="none" />
        </EditingHelpText>
        {card}
      </div>
    );
  }
  return card;
}

export const getComponentServerProps: GetComponentServerProps = async (rendering, _layoutData) => {
  const gridAspectRatio = getEnumValue<ImageAspectRatio>(
    rendering.params?.gridAspectRatio as unknown as Parameters<typeof getEnumValue>[0]
  );
  const cmHost = process.env.NEXT_PUBLIC_SITECORE_HOST ?? '';
  const fallbackImagePath =
    CARD_FALLBACK_IMAGE_PATHS[gridAspectRatio ?? DEFAULT_CARD_FALLBACK_ASPECT_RATIO];

  return {
    staticProps: {
      fallbackImageUrl: cmHost ? `${cmHost}${fallbackImagePath}` : fallbackImagePath,
    },
  };
};
